import { motion } from 'framer-motion';
import { CalendarDaysIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface SobrietyCardProps {
  label: string;
  startDate: Date;
  color?: string;
}

function calculateSobrietyTime(startDate: Date) {
  const now = new Date();
  const diffMs = now.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);
  const days = diffDays % 30;

  return { days: diffDays, years, months, remainingDays: days };
}

function getMilestone(days: number): string | null {
  const milestones = [1, 7, 14, 30, 60, 90, 180, 365, 730, 1095, 1825, 3650];
  const recentMilestone = milestones.filter(m => days >= m && days < m + 7).pop();

  if (recentMilestone) {
    if (recentMilestone === 1) return '1 Day!';
    if (recentMilestone === 7) return '1 Week!';
    if (recentMilestone === 14) return '2 Weeks!';
    if (recentMilestone === 30) return '1 Month!';
    if (recentMilestone === 60) return '2 Months!';
    if (recentMilestone === 90) return '90 Days!';
    if (recentMilestone === 180) return '6 Months!';
    if (recentMilestone === 365) return '1 Year!';
    if (recentMilestone === 730) return '2 Years!';
    if (recentMilestone === 1095) return '3 Years!';
    if (recentMilestone === 1825) return '5 Years!';
    if (recentMilestone === 3650) return '10 Years!';
  }
  return null;
}

export function SobrietyCard({ label, startDate, color = '#14b8a6' }: SobrietyCardProps) {
  const { days, years, months, remainingDays } = calculateSobrietyTime(startDate);
  const milestone = getMilestone(days);

  return (
    <motion.div
      className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700 shadow-sm"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <CalendarDaysIcon className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <h3 className="font-semibold text-surface-800 dark:text-white">{label}</h3>
            {milestone && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-600 dark:text-accent-400">
                <TrophyIcon className="w-3 h-3" />
                {milestone}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main counter */}
      <div className="text-center mb-4">
        <div className="text-5xl font-bold text-surface-900 dark:text-white mb-1">
          {days.toLocaleString()}
        </div>
        <div className="text-sm text-surface-500 dark:text-surface-400">days</div>
      </div>

      {/* Breakdown */}
      {days >= 30 && (
        <div className="flex justify-center gap-6 text-center border-t border-surface-100 dark:border-surface-700 pt-4">
          {years > 0 && (
            <div>
              <div className="text-lg font-semibold text-surface-800 dark:text-white">{years}</div>
              <div className="text-xs text-surface-500">{years === 1 ? 'year' : 'years'}</div>
            </div>
          )}
          <div>
            <div className="text-lg font-semibold text-surface-800 dark:text-white">{months}</div>
            <div className="text-xs text-surface-500">{months === 1 ? 'month' : 'months'}</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-surface-800 dark:text-white">{remainingDays}</div>
            <div className="text-xs text-surface-500">days</div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
